<script>
import Contributor from '@/components/organism/Contributor'
import InputCheckbox from '@/components/atoms/inputlib/components/InputCheckbox'
import Box from '@/components/atoms/Box'
import Spinner from '@/components/atoms/Spinner'
import ButtonLoader from '@/components/atoms/ButtonLoader'
import { mapGetters } from 'vuex'
import isEqual from 'lodash.isequal'

export default {
    name: 'GroupOrderBlock',
    components: {
        Spinner,
        Box,
        InputCheckbox,
        Contributor,
        ButtonLoader,
    },
    props: {
        checkout: Boolean,
        expenseRuleId: null,
    },
    data() {
        return {
            isGroupOrder: false,
            loading: false,
            highlight: false,
            backup: null,
            newContributor: null,
            saveNewContributor: false,
        }
    },
    computed: {
        ...mapGetters({
            lgDevice: 'lgDevice',
            currency: 'active/currency',
            config: 'site/config',
        }),
        cart() {
            return this.$cart
        },
        calculation() {
            return this.$cart.calculation
        },
        calculationError() {
            return this.calculation.validation_errors.find(
                (error) => error.propertyPath === 'groupOrderSumDistribution'
            )
        },
        employees() {
            return this.cart.canStandardGroupOrder || this.cart.canCanteenGroupOrder
                ? Object.entries(this.cart.groupOrderBalances)
                      .map(([email, budget]) => {
                          return {
                              email: email,
                              budget: budget,
                              contribution: 0,
                              removable: true,
                              name: this.cart.groupOrderNames[email],
                          }
                      })
                      .filter(
                          (employee) =>
                              !this.selection.some((selected) => employee.email === selected.email)
                      )
                : []
        },
        selection() {
            if (!this.cart.calculated) return []
            return Object.entries(this.calculation.group_order_sum_distribution).reduce(
                (p, [email, contribution]) => {
                    if (email === '') {
                        p[0] = {
                            email: this.$user.model.email,
                            budget: this.cart.groupOrderPersonalBudget,
                            contribution: contribution || 0,
                            removable: false,
                            name: this.$user.name,
                        }
                    } else {
                        p[this.cart.group_order_employees.indexOf(email) + 1] = {
                            email: email,
                            budget: this.cart.groupOrderBalances[email],
                            contribution: contribution || 0,
                            removable: true,
                            name: this.cart.groupOrderNames[email],
                        }
                    }
                    return p
                },
                []
            )
        },
        remainingBudget() {
            return (
                (this.selection.length ? this.selection.reduce((p, c) => p + c.budget, 0) : 0) -
                this.usedBudget
            )
        },
        usedBudget() {
            return this.selection.length
                ? this.selection.reduce((p, c) => p + c.contribution, 0)
                : 0
        },
        price() {
            return this.calculation.price_sum
        },
        overflow() {
            return this.price - this.usedBudget
        },
        saved() {
            return this.cart.isGroupSaved
        },
        isHu() {
            return this.config?.country !== 'hu'
        },
    },
    mounted() {
        this.isGroupOrder = Boolean(
            (this.cart.isStandardGroupOrder &&
                this.calculation.selected_expense_rule.id === this.expenseRuleId) ||
                this.cart.isCanteenGroupOrder
        )
        if ((this.checkout && this.isGroupOrder) || !this.checkout)
            this.$nextTick(() => {
                this.init()
            })
        this.cart.on('price', this.onPrice)
    },
    beforeDestroy() {
        this.cart.off('price', this.onPrice)
    },
    methods: {
        init() {
            this.backup = {
                group_order_employees: [...this.cart.group_order_employees],
                group_order_sum_distribution: {
                    ...this.cart.group_order_sum_distribution,
                },
            }
        },
        async save(loading = true) {
            this.loading = loading
            try {
                await this.cart.calculate()
                Object.entries(this.cart.calculation.group_order_sum_distribution).forEach(
                    ([key, value]) => {
                        if (this.cart.group_order_sum_distribution[key])
                            this.$set(this.cart.group_order_sum_distribution, key, value)
                    }
                )
            } catch (e) {
                if (!this.checkout) this.$emit('error')
                throw e
            } finally {
                this.loading = false
            }
        },
        addContributor() {
            this.newContributor = {}
        },
        async saveContributor(contributor) {
            this.saveNewContributor = true
            this.newContributor = contributor
            try {
                if (!this.cart.group_order_employees)
                    this.$set(this.$cart.model, 'group_order_employees', [])

                this.$cart.group_order_employees.push(contributor.email)
                await this.save(false)
                this.newContributor = null
            } finally {
                this.saveNewContributor = false
            }
        },
        resetNewContributor() {
            this.newContributor = null
            this.saveNewContributor = false
        },
        removeContributor(contributor) {
            if (contributor.removable) {
                this.cart.group_order_employees.splice(
                    this.cart.group_order_employees.indexOf(contributor.email),
                    1
                )
                delete this.cart.group_order_sum_distribution[contributor.email]
                this.save()
            }
        },
        setContribution(contributor, contribution) {
            if (!this.cart.group_order_sum_distribution)
                this.$cart.group_order_sum_distribution = {}
            this.$cart.group_order_sum_distribution[
                contributor.email === this.$user.model.email ? '' : contributor.email
            ] = contribution
            this.save()
        },
        evenContribution() {
            this.$set(this.$cart.model, 'group_order_sum_distribution', {})
            this.save()
        },
        discard() {
            if (
                !isEqual(this.cart.group_order_employees, this.backup.group_order_employees) ||
                !isEqual(
                    this.cart.group_order_sum_distribution,
                    this.backup.group_order_sum_distribution
                )
            ) {
                this.cart.group_order_employees = this.backup.group_order_employees
                this.cart.group_order_sum_distribution = this.backup.group_order_sum_distribution
                this.save()
            }
        },
        reset() {
            if (!this.isGroupOrder || !this.checkout) {
                this.cart.resetGroupOrder().finally(() => {
                    this.cart.calculate()
                })
            }
        },
        toggleState(state) {
            if (state) this.init()
            else this.reset()
        },
        attention() {
            this.highlight = true
            setTimeout(() => {
                this.highlight = false
            }, 2000)
        },
    },
}
</script>

<template lang="pug">
.group-order-block
    .d-flex.flex-row.align-items-center(v-if="checkout")
        input-checkbox.mr-2(v-model="isGroupOrder" @input="toggleState"  switcher)
        span.text-sm.font-weight-semibold.mr-2.line-1 {{$t('order.group_order')}}&nbsp;
        span.text-sm.line-1 {{$t('order.group_pool')}}
        transition(name="fade" mode="out-in" v-if="isGroupOrder")
            spinner(v-if="loading" size="20")
    box(:value="checkout && isGroupOrder || !checkout" slim)
        div(v-if="selection.length" :class="{'mt-5': checkout}")
            .d-flex.justify-content-between.mb-2
                span {{$t('order.colleague')}}
                button.btn.btn-link-dark(@click="evenContribution" :disabled="loading") {{$t('order.contribution')}}
            .scroll-y-container(:class="{'max-h-vh-50':lgDevice}")
                contributor(
                    v-for="(contributor, index) in selection"
                    :value="contributor.contribution"
                    :contributor="contributor"
                    :currency="currency"
                    :error="contributor.contribution > price"
                    :manual-price="cart.group_order_sum_distribution[contributor.removable ? contributor.email : ''] !== undefined"
                    :key="`contributor-${index}`"
                    :readonly="loading || selection.length < 2"
                    @input="setContribution(contributor, $event)"
                    @remove="removeContributor(contributor)")
            contributor(
                v-if="newContributor"
                :contributor="newContributor"
                :currency="currency"
                :highlight="highlight"
                :employees="employees"
                :loading="saveNewContributor"
                select
                @select="saveContributor"
                @remove="resetNewContributor")

            .row.mx-n2
                .col-12.col-md-5.col-xxl-8.px-2.mb-4.mb-xl-0
                    transition(name="fade")
                        button.btn.btn-link-dark(
                            v-if="employees.length || cart.group_order_employees.indexOf(undefined) > -1"
                            @click="addContributor" 
                            :disabled="loading")  {{$t('order.add_group_member')}}
                .col-12.col-md-7.col-xxl-4.px-2
                    .d-flex.justify-content-between.mb-2
                        span.font-weight-semibold {{$t('order.total_contr')}}
                        span.font-weight-semibold 
                            transition(name="fade")
                                icon.mr-2.text-danger.cursor-pointer(v-if="calculationError" v-tooltip.hover.top="calculationError.message" icon="fa-solid fa-exclamation-triangle")
                            | {{usedBudget | toCurrency(currency.locale, currency.currency_code)}}
                    .d-flex.justify-content-between
                        span {{$t('order.available')}}
                        span {{remainingBudget  | toCurrency(currency.locale, currency.currency_code)}}                  
            p.body-sm.text-73.text-center.mb-0.mt-5 {{$t('order.group_desc')}}
</template>
<style lang="scss">
.group-order-block {
    &,
    .tile {
        display: flex;
        height: 100%;
        flex-direction: column;
    }
    .box {
        height: calc(100% - 27px);
    }
    .box-body {
        height: 100%;
    }
}
</style>
